/**
 * Market Intelligence product page — BP-024.
 * ZIP-level health, strategy-fit, forecasts, and alerts.
 */
import { LineChart, Map, TrendingUp, Bell, Compass, Target } from "lucide-react";
import ProductPageTemplate, { ProductScreen } from "../ProductPageTemplate";

const heroMockup = (
  <ProductScreen title="market intel / 44107 · Lakewood OH">
    <div className="grid grid-cols-3 gap-2 text-xs">
      {[
        ["Health", "83", "Hot"],
        ["Vol Trend", "+14%", "90d"],
        ["Days on Mkt", "18", "-6"],
        ["Rent Yield", "9.2%", "+0.4"],
        ["Cap Rate", "8.6%", "stable"],
        ["Forecast", "+3.1%", "12mo"],
      ].map(([k, v, s]) => (
        <div key={k} className="rounded-lg border border-slate-200 p-2">
          <div className="uppercase text-[9px] text-slate-500">{k}</div>
          <div className="text-base font-bold text-slate-900">{v}</div>
          <div className="text-[9px] text-emerald-600 font-semibold mt-0.5">{s}</div>
        </div>
      ))}
    </div>
    <div className="mt-3 pt-3 border-t border-slate-200 text-[10px]">
      <div className="font-semibold text-slate-800">Strategy fit</div>
      <div className="mt-1 flex gap-1 flex-wrap">
        {["Wholesale ●●●●○", "BRRRR ●●●●●", "Flip ●●●○○", "Buy&Hold ●●●●●"].map((s) => (
          <span key={s} className="rounded bg-slate-100 px-2 py-0.5 font-mono">{s}</span>
        ))}
      </div>
    </div>
  </ProductScreen>
);

export default function MarketIntelligencePage() {
  return (
    <ProductPageTemplate
      slug="market-intelligence"
      bp="BP-024 · Market Intelligence Engine"
      eyebrow="Product · Market Intelligence"
      title="Every ZIP, scored."
      titleAccent="Every strategy, ranked."
      subtitle="A deterministic engine that ingests MLS velocity, rent yields, inventory tightness, and forecasts to produce a Market Health Score, strategy-fit ranking, and 12-month directional forecast for every ZIP you touch."
      bullets={[
        "0–100 Market Health Score with elite / strong / healthy / watch bands",
        "Strategy-fit ranking per market — see BRRRR-friendly vs Flip-friendly ZIPs at a glance",
        "Alerts when a market's health drops or an opportunity window opens",
      ]}
      heroMockup={heroMockup}
      capabilities={[
        { icon: Map, title: "ZIP-Level Coverage", desc: "42,000+ ZIP codes scored nightly. State, MSA, and County rollups included." },
        { icon: LineChart, title: "12-Month Forecast", desc: "Directional forecast on price, rent, and volume — with confidence intervals." },
        { icon: Target, title: "Strategy Fit", desc: "Every ZIP ranked for Wholesale, BRRRR, Flip, and Buy-and-Hold suitability separately." },
        { icon: TrendingUp, title: "Momentum Signals", desc: "90-day velocity, DOM compression, rent-growth acceleration — spot inflections before spreadsheets." },
        { icon: Bell, title: "Opportunity Alerts", desc: "Get notified the day a ZIP crosses into 'Hot' or slips into 'Watch'." },
        { icon: Compass, title: "Expansion Copilot", desc: "\"Show me 10 ZIPs like 44107 in Texas\" — copy your winners into new geographies." },
      ]}
      howItWorks={[
        { step: 1, title: "Ingest", desc: "MLS velocity, rent data, permit filings, foreclosure activity feed the engine nightly." },
        { step: 2, title: "Score", desc: "Deterministic scoring model produces a 0–100 Health Score per ZIP." },
        { step: 3, title: "Rank", desc: "Each ZIP is ranked separately for four investor strategies." },
        { step: 4, title: "Alert", desc: "Threshold crossings and forecast inflections trigger notifications automatically." },
      ]}
      integrations={[
        { bp: "BP-024", name: "Market Intelligence" },
        { bp: "BP-004", name: "ARV Comps" },
        { bp: "BP-014", name: "Strategy Comparison" },
        { bp: "BP-005", name: "Deal Score" },
        { bp: "BP-023", name: "Portfolio Overlay" },
        { bp: "BP-025", name: "Coach Cites Market" },
        { bp: "BP-018", name: "Buyer Marketing" },
        { bp: "BP-019", name: "Exec Dashboard" },
      ]}
      proof={{
        stat: "9",
        label: "new ZIPs identified in first 30 days",
        quote: "We used to guess which secondary markets to enter. Now the ranker just tells us — and it's cited every step of the way.",
        attribution: "Managing partner · Fix-and-flip fund",
      }}
      related={[
        { title: "Portfolio Intelligence", to: "/products/portfolio-intelligence", desc: "See how your held assets track vs. their local market." },
        { title: "Strategy Comparison", to: "/products/strategy-comparison", desc: "Combine market signals with property-specific exit scoring." },
        { title: "AI Acquisition Coach", to: "/products/ai-acquisition-coach", desc: "The Coach cites BP-024 in every recommendation." },
      ]}
    />
  );
}
